import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import DesignOption from '@design-system/option';
import DesignStepper from '@design-system/stepper';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as stundentActions } from '@store/slice/student-slice';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { IStudyConfigurationModalData, EStudyConfigurationMode, TTest } from '@datatype/iyagigo/type';
import { ETheme } from '@datatype/type';
import { useModifyStudentMutation } from '@hook/iyagigo/useApi';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import Gap from '@component/common/gap';
import TextButton from '@design-system/text-button';

import { v4 as uuid4 } from 'uuid';
import {
  GRAMMAR_COUNT_OPTIONS,
  STUDY_LANGUAGE_OPTIONS,
  TEST_COUNT_OPTIONS,
  TOEIC_LEVEL_OPTIONS,
  VOCABULARY_COUNT_OPTIONS,
  JLPT_LEVEL_OPTIONS,
  VOCABULARY_LEVEL_OPTIONS,
  GRAMMAR_LEVEL_OPTIONS,
} from '@constant/variable';
import { EApiClozeLevel, EApiLanguageCode, EApiTest, IModifyStudentRequest } from '@datatype/iyagigo/api-type';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';
import { useNavigate, useLocation } from 'react-router-dom';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';

interface IProps {
  data: IStudyConfigurationModalData['data'];
}

// ############### type ###############
const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const OptionContainer = styled.div<{ $direction: 'row' | 'column' }>`
  display: flex;
  flex-flow: ${(props) => props.$direction} wrap;
  justify-content: ${(props) => (props.$direction === 'row' ? 'space-between' : 'flex-start')};
  gap: ${(props) => (props.$direction === 'column' ? '5px' : '0px')};
  width: 100%;
`;

const ClozeStudyConfigurationModal: React.FC<IProps> = ({ data }) => {
  // ############### route ###############
  const navigate = useNavigate();
  const location = useLocation();

  // ############### variable ###############
  const { mode } = data;
  const isClozeLevelMode = [
    EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL,
    EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL,
    EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL,
    EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL,
  ].includes(mode);

  // ############### conversion ###############
  const convertStudyTestByString = (level: string): TTest => {
    switch (level) {
      case 'toeic950':
        return EApiTest.TOEIC950;
      case 'toeic850':
        return EApiTest.TOEIC850;
      case 'toeic750':
        return EApiTest.TOEIC750;
      case 'toeic650':
        return EApiTest.TOEIC650;
      case 'toeic550':
        return EApiTest.TOEIC550;
      case 'jlpt1':
        return EApiTest.JLPT1;
      case 'jlpt2':
        return EApiTest.JLPT2;
      case 'jlpt3':
        return EApiTest.JLPT3;
      case 'jlpt4':
        return EApiTest.JLPT4;
      case 'jlpt5':
        return EApiTest.JLPT5;
      default:
        throw new Error(`invalid test -> ${level}`);
    }
  };

  // ############### state ###############
  const dispatch = useTypedDispatch();
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;

  const englishVocabularyClozeLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentEnglishVocabularyLevel,
  ) ?? null;
  const englishGrammarClozeLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentEnglishGrammarLevel,
  ) ?? null;
  const japaneseVocabularyClozeLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentJapaneseVocabularyLevel,
  ) ?? null;
  const japaneseGrammarClozeLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentJapaneseGrammarLevel,
  ) ?? null;

  const [optionKey, setOptionCount] = useState<
    | (typeof VOCABULARY_COUNT_OPTIONS)[number]['key']
    | (typeof GRAMMAR_COUNT_OPTIONS)[number]['key']
    | (typeof VOCABULARY_LEVEL_OPTIONS)[number]['key']
    | (typeof GRAMMAR_LEVEL_OPTIONS)[number]['key']
    | (typeof TEST_COUNT_OPTIONS)[number]['key']
    | null
  >(null);

  const [testLevel, setTestLevel] = useState<
    (typeof TOEIC_LEVEL_OPTIONS)[number]['key'] | (typeof JLPT_LEVEL_OPTIONS)[number]['key'] | null
  >(null);
  const [languageConfiguration, setLanguageConfiguration] = useState<
    (typeof STUDY_LANGUAGE_OPTIONS)[number]['key'] | null
  >(null);

  const getIsSavable = () => {
    // 학습언어 설정
    if (mode === EStudyConfigurationMode.STUDY_LANGUAGE) return languageConfiguration !== null;
    // 학습난이도 및 시험설정
    if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL
    ) {
      return optionKey !== null;
    }
    if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL
    ) {
      return testLevel !== null;
    }
    // 학습목표(개수) 설정
    if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT
    ) {
      return optionKey !== null;
    }
  };

  // ############### api ###############
  const mutation = useModifyStudentMutation();
  const subscriptionQuery = useMySubscriptionQuery();
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const isSubscriber = subscriptionQuery.data?.data?.status === EApiSubscriptionStatus.ACTIVE;

  // ############### variable ###############
  const OPTIONS = (() => {
    // 학습언어 설정
    if (mode === EStudyConfigurationMode.STUDY_LANGUAGE) return STUDY_LANGUAGE_OPTIONS;
    // 학습난이도 및 시험설정
    if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL
    ) {
      return VOCABULARY_LEVEL_OPTIONS;
    }
    if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL
    ) {
      return GRAMMAR_LEVEL_OPTIONS;
    }
    if (mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL) return TOEIC_LEVEL_OPTIONS;
    if (mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL) return JLPT_LEVEL_OPTIONS;

    // 학습목표(개수) 설정
    if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT
    )
      return VOCABULARY_COUNT_OPTIONS;
    if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT
    )
      return GRAMMAR_COUNT_OPTIONS;
    if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT
    )
      return TEST_COUNT_OPTIONS;
    return [];
  })();

  const NOTIFICATION_TEXT = (() => {
    // 학습언어 설정
    if (mode === EStudyConfigurationMode.STUDY_LANGUAGE) {
      return `${isAuthenticated ? '' : ''}학습언어를 선택해주세요`;
    }
    // 학습난이도 및 시험설정
    if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL
    ) {
      return '학습난이도를 선택해주세요';
    }
    if (mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL) return 'TOEIC 학습목표를 설정해주세요';
    if (mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL) return 'JLPT 학습목표를 설정해주세요';

    // 학습목표(개수) 설정
    if (mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT) return '영어 어휘 일일목표를 설정해주세요';
    if (mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT) return '일본어 어휘 일일목표를 설정해주세요';

    if (mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT) return '영어 문법 일일목표를 설정해주세요';
    if (mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT) return '일본어 문법 일일목표를 설정해주세요';

    if (mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT) return 'TOEIC 문제 일일목표를 설정해주세요';
    if (mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT) return 'JLPT 문제 일일목표를 설정해주세요';

    return '';
  })();

  const Options = OPTIONS.map((item) => {
    const optionSize = OPTIONS.length;
    const handleClick = () => {
      if (mode === EStudyConfigurationMode.STUDY_LANGUAGE) {
        setLanguageConfiguration(item.key);
      } else if (
        mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL
      ) {
        setTestLevel(item.key);
      } else if (
        mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT ||
        mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT ||
        mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT
      ) {
        setOptionCount(item.key);
      } else if (
        mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
        mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL
      ) {
        setOptionCount(item.key);
      }
    };

    const getIsSelected = () => {
      if (mode === EStudyConfigurationMode.STUDY_LANGUAGE) return item.key === languageConfiguration;
      if (
        mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL
      )
        return item.key === testLevel;
      if (
        mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT ||
        mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT ||
        mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT
      ) {
        return item.key === optionKey;
      }
      if (
        mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
        mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL
      ) {
        return item.key === optionKey;
      }
    };

    const isTestLevel =
      mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL;

    const isClozeLevel =
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL;

    return (
      <DesignOption
        key={uuid4()}
        theme={ETheme.BROWN}
        width={isTestLevel || isClozeLevel ? '100%' : `calc(100% / ${optionSize} - 5px)`}
        label={isTestLevel ? undefined : item.label}
        value={item.content}
        selected={getIsSelected()}
        handleClick={handleClick}
      />
    );
  });

  // ############### handler ###############
  const handleSaveStudyLanugageButtonClick = async () => {
    if (languageConfiguration === null) return;
    const targetLanguage = languageConfiguration === 'english' ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;

    if (studentKey) {
      await mutation.mutateAsync({
        studentKey,
        studyLanguage: targetLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
      });
    }

    if (location.pathname !== IYAGIGO_PATH.STUDIO_CONFIGURATION_PATH) {
      dispatch(stundentActions.storeCurrentStudyLanguage(targetLanguage));
      navigate(IYAGIGO_PATH.INITIAL_SERVICE_PATH);
    }
    dispatch(modalActions.hideModal());
  };

  const handleSaveCountButtonClick = async () => {
    if (!optionKey) return;

    const getGoalValue = () => {
      if (
        mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT
      ) {
        return VOCABULARY_COUNT_OPTIONS.find((opt) => opt.key === optionKey)?.value;
      }
      if (
        mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT
      ) {
        return GRAMMAR_COUNT_OPTIONS.find((opt) => opt.key === optionKey)?.value;
      }
      if (
        mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT ||
        mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT
      ) {
        return TEST_COUNT_OPTIONS.find((opt) => opt.key === optionKey)?.value;
      }
      return 0;
    };

    const limitGoal = getGoalValue() ?? 0;
    const isEnglish =
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT;
    const isVocabulary =
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT;
    const isGrammar =
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT;
    const isTest =
      mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT;

    if (studentKey) {
      const payload: Partial<IModifyStudentRequest> = {
        studentKey,
        englishVocabularyClozeGoal: isEnglish && isVocabulary ? limitGoal : undefined,
        englishGrammarClozeGoal: isEnglish && isGrammar ? limitGoal : undefined,
        japaneseVocabularyClozeGoal: !isEnglish && isVocabulary ? limitGoal : undefined,
        japaneseGrammarClozeGoal: !isEnglish && isGrammar ? limitGoal : undefined,
        ...(isTest &&
          (isEnglish
            ? {
                toeic950TestGoal: limitGoal,
                toeic850TestGoal: limitGoal,
                toeic750TestGoal: limitGoal,
                toeic650TestGoal: limitGoal,
                toeic550TestGoal: limitGoal,
              }
            : {
                jlpt1TestGoal: limitGoal,
                jlpt2TestGoal: limitGoal,
                jlpt3TestGoal: limitGoal,
                jlpt4TestGoal: limitGoal,
                jlpt5TestGoal: limitGoal,
              })),
      };

      await mutation.mutateAsync(payload as IModifyStudentRequest);
    }
    dispatch(modalActions.hideModal());
  };

  const handleSaveStudyTestButtonClick = async () => {
    if (!testLevel) return;
    const studyTest = convertStudyTestByString(testLevel);
    if (!studyTest) return;
    const isToeic = studyTest.startsWith('TOEIC');
    const isJlpt = studyTest.startsWith('JLPT');

    if (studentKey) {
      await mutation.mutateAsync({
        studentKey,
        toeicTestLevel: isToeic ? studyTest : undefined,
        jlptTestLevel: isJlpt ? studyTest : undefined,
      });
    }

    if (isToeic) dispatch(stundentActions.storeCurrentToeicTest(studyTest));
    if (isJlpt) dispatch(stundentActions.storeCurrentJlptTest(studyTest));

    dispatch(modalActions.hideModal());
  };

  const handleSaveClozeLevelButtonClick = async () => {
    if (!optionKey) return;
    const isEnglish =
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL;
    const isJapanese =
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL;
    const isVocabulary =
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL;
    const isGrammar =
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL;

    const selectedValue = (OPTIONS as any).find((opt: any) => opt.key === optionKey)?.value as EApiClozeLevel;

    if (studentKey) {
      await mutation.mutateAsync({
        studentKey,
        englishVocabularyClozeLevel: isEnglish && isVocabulary ? selectedValue : undefined,
        englishGrammarClozeLevel: isEnglish && isGrammar ? selectedValue : undefined,
        japaneseVocabularyClozeLevel: isJapanese && isVocabulary ? selectedValue : undefined,
        japaneseGrammarClozeLevel: isJapanese && isGrammar ? selectedValue : undefined,
      });
    }

    if (isVocabulary) {
      if (isEnglish) dispatch(stundentActions.storeCurrentEnglishVocabularyLevel(selectedValue));
      if (isJapanese) dispatch(stundentActions.storeCurrentJapaneseVocabularyLevel(selectedValue));
    } else if (isGrammar) {
      if (isEnglish) dispatch(stundentActions.storeCurrentEnglishGrammarLevel(selectedValue));
      if (isJapanese) dispatch(stundentActions.storeCurrentJapaneseGrammarLevel(selectedValue));
    }

    dispatch(modalActions.hideModal());
  };

  const handeSaveButtonClick = () => {
    if (isClozeLevelMode && !isSubscriber) {
      dispatch(
        modalActions.showWallToast({
          type: 'subscription-wall',
          data: { trigger: 'access' },
        }),
      );
      return;
    }

    if (mode === EStudyConfigurationMode.STUDY_LANGUAGE) {
      handleSaveStudyLanugageButtonClick();
    } else if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL
    ) {
      handleSaveStudyTestButtonClick();
    } else if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL
    ) {
      handleSaveClozeLevelButtonClick();
    } else if (
      mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT ||
      mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT ||
      mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT
    ) {
      handleSaveCountButtonClick();
    }
  };

  // 현재 위치가 랜딩 페이지인지 확인
  const isLandingPage = location.pathname === IYAGIGO_PATH.LANDING_PATH;
  const buttonText = isLandingPage ? '시작하기' : '저장하기';

  return (
    <Wrapper>
      <TextBlock
        text={NOTIFICATION_TEXT}
        design={{
          text: { size: 'medium', color: color.BLACK_2 },
          box: { alignX: 'center' },
        }}
      />
      <Gap height={size.MARGIN_1} />
      <OptionContainer
        $direction={
          mode === EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL ||
          mode === EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL ||
          mode === EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL ||
          mode === EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL ||
          mode === EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL ||
          mode === EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL
            ? 'column'
            : 'row'
        }>
        {Options}
      </OptionContainer>
      <Gap height={size.MARGIN_2} />
      <TextButton text={buttonText} theme={ETheme.BROWN} size='medium' shape='pill' width='100%' disabled={!getIsSavable()} handleClick={handeSaveButtonClick} />
    </Wrapper>
  );
};

export default ClozeStudyConfigurationModal;
